import { useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack, Shelf, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Form, Dropdown as CommonDropdown } from "capitalrx-components-2.0/lib/forms";
import { queryString } from "capitalrx-utilities";

import { useGetCoverageTypeQuery } from "@/api/coverageStrategy";
import { useGetGeoLocationMutation } from "@/api/geoLocation";
import { PageTitleWrapper, Dropdown, SubmitButton, ZipCodeTextbox } from "@/basic/components";
import { StickyLayout } from "@/basic/layouts";

interface FormDataType {
  coverage: string;
  zip_code: string;
}

export const SelectPlan = () => {
  const { t: translate } = useTranslation();
  const showAlert = useAlert();

  const form = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [getGeoLocation, { isError: isGeoLocationError }] = useGetGeoLocationMutation();
  const getCurrentQueryStrings = queryString.convertQueryStringToJson(location.search);

  const failureHandler = useCallback(() => {
    showAlert({ type: "error", style: "toast", message: translate("SELECT_PLAN.anErrorOcurred") });
  }, [showAlert]);

  const {
    data: coverageType,
    isLoading,
    isError: isCoverageTypeQueryError,
  } = useGetCoverageTypeQuery({
    client: getCurrentQueryStrings.client.toString(),
  });

  if (isGeoLocationError || isCoverageTypeQueryError) {
    failureHandler();
  }

  const { mainContainer, spacer } = useResponsive(
    useMemo(
      () => ({
        mainContainer: { desktop: 4, tablet: 6, mobile: 12 },
        spacer: { desktop: 4, tablet: 3, mobile: 0 },
      }),
      [],
    ),
  );

  const handleSubmit = async (formData: FormDataType): Promise<void> => {
    const userGeoLocation = await getGeoLocation({ zip_code: formData.zip_code });

    const getCurrentQueryStrings = queryString.convertQueryStringToJson(location.search);

    // @ts-ignore
    const lat = userGeoLocation?.data?.latitude;
    // @ts-ignore
    const lng = userGeoLocation?.data?.longitude;

    const isValidLocation = !!lat && !!lng;

    if (isValidLocation || isGeoLocationError) {
      const params = {
        coverage: formData.coverage,
        zip_code: formData.zip_code,
        // @ts-ignore
        lat: userGeoLocation?.data?.latitude,
        // @ts-ignore
        lng: userGeoLocation?.data?.longitude,
      };

      const urlParams = queryString.convertJsonToQueryString({ ...getCurrentQueryStrings, ...params });

      navigate({
        pathname: "/pharmacy/list",
        search: urlParams,
      });
    } else {
      form?.current?.setError("zip_code", { type: "custom", message: translate("SELECT_PLAN.invalidZipCode") });
    }
  };

  return (
    <StickyLayout testId="test__home-page-wrapper">
      <Stack spacing="large">
        <Shelf>
          <PageTitleWrapper title={translate("SELECT_PLAN.findLocalPharmacies")}></PageTitleWrapper>
        </Shelf>

        <Form<FormDataType> onSubmit={handleSubmit} methodsRef={form}>
          <Grid>
            <Grid.Span columns={spacer} />
            <Grid.Spread columns={mainContainer}>
              <Stack spacing="medium">
                <Dropdown name="coverage" label={translate("SELECT_PLAN.selectYourPlanType")} defaultValue="commercial" readOnly={isLoading} required>
                  {coverageType
                    && Object.entries(coverageType).map(([key, value]) => {
                      return (
                        <CommonDropdown.Option value={key} key={key}>
                          {value}
                        </CommonDropdown.Option>
                      );
                    })}
                </Dropdown>

                <ZipCodeTextbox name="zip_code" label={translate("SELECT_PLAN.zipCode")} required readOnly={isLoading} />

                <SubmitButton>{translate("SELECT_PLAN.search")}</SubmitButton>
              </Stack>
            </Grid.Spread>
            <Grid.Span columns={spacer} />
          </Grid>
        </Form>
      </Stack>
    </StickyLayout>
  );
};
